import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  HStack,
  Text,
  IconButton,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Avatar,
  AvatarBadge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  useBreakpointValue,
  Spinner,
} from "@chakra-ui/react";
import { useToast } from "@chakra-ui/react";
import {
  Bell,
  Settings,
  LogOut,
  User,
  ChevronRight,
  Menu as MenuIcon,
} from "lucide-react";
import { motion } from "framer-motion";
import { useHistory } from "react-router-dom";

const MotionBox = motion(Box);

export default function AdminNavbar({ brandText, isSidebarMinimized, onOpen }) {
  const [scrolled, setScrolled] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const history = useHistory();

  const isMobile = useBreakpointValue({ base: true, lg: false });

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const storage = localStorage.getItem("authToken")
      ? localStorage
      : sessionStorage;

    const name = storage.getItem("userName");
    const email = storage.getItem("email") || "No Email";
    const id = storage.getItem("userId");

    setUserData({ name, email, id });
    setLoading(false);
  }, []);
  const confirmSignOut = () => {
    localStorage.clear();
    sessionStorage.clear();

    toast({
      title: "Signed Out",
      description: "You have been logged out successfully",
      status: "success",
      duration: 1000,
      isClosable: true,
      position: "top",
    });
    history.push("/auth/signin");
  };

  const sidebarOffset = isSidebarMinimized ? "80px" : "280px";

  return (
    <MotionBox
      position="fixed"
      top="0"
      left={{ base: 0, lg: sidebarOffset }}
      right="0"
      zIndex="997"
      bg={scrolled ? "rgba(248, 3, 3, 0.7)" : "red.600"}
      backdropFilter="blur(20px)"
      borderBottom="1px solid"
      borderColor={scrolled ? "rgba(212,175,55,0.3)" : "transparent"}
      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      py={4}
      px={{ base: 4, md: 8 }}
      w={{ base: "100%", lg: `calc(100% - ${sidebarOffset})` }}
    >
      <Flex align="center" justify="space-between" maxW="1400px" mx="auto">
        {/* Left */}
        <Flex align="center" flex={1}>
          {/* Mobile Sidebar Button */}
          {isMobile && (
            <IconButton
              icon={<MenuIcon size={24} />}
              variant="ghost"
              color="white"
              onClick={onOpen}
              mr={4}
              _hover={{ bg: "rgba(212,175,55,0.1)" }}
            />
          )}

          {/* Breadcrumbs */}
          <Box>
            <Breadcrumb
              spacing="8px"
              separator={<ChevronRight size={16} color="#D4AF37" />}
              color="#F4F4F4"
              fontSize="sm"
            >
              <BreadcrumbItem>
                <BreadcrumbLink>
                  Dashboard / {brandText || "Overview"}
                </BreadcrumbLink>
              </BreadcrumbItem>
            </Breadcrumb>

            <Text
              fontSize={["lg", "xl", "1.5xl"]}
              fontWeight="bold"
              color="white"
              mt={1}
            >
              {brandText || "Dashboard"}
            </Text>
          </Box>
        </Flex>

        {/* Right */}
        <HStack spacing={4}>
          {/* Profile Dropdown */}
          <Menu>
            <MenuButton>
              <HStack spacing={3}>
                <Avatar size="sm" bg="#D4AF37" color="#0D1B2A">
                  <AvatarBadge
                    boxSize="1.3em"
                    bg="green.500"
                    border="2px solid #0D1B2A"
                  />
                </Avatar>

                {!isMobile && (
                  <Box textAlign="left">
                    <Text fontSize="md" fontWeight="medium" color="white">
                      {loading ? (
                        <Spinner size="xs" />
                      ) : (
                        userData?.name || "Admin"
                      )}
                    </Text>
                    {/* <Text fontSize="xs" color="#B0B0B0">
                      {userData?.email}
                    </Text> */}
                  </Box>
                )}
              </HStack>
            </MenuButton>

            <MenuList
              bg="#0D1B2A"
              border="1px solid rgba(212,175,55,0.3)"
              shadow="xl"
            >
              <MenuDivider borderColor="rgba(255,255,255,0.1)" />

              <MenuItem
                icon={<LogOut size={18} />}
                color="red.400"
                _hover={{ bg: "rgba(255,0,0,0.1)" }}
                onClick={confirmSignOut}
              >
                Sign Out
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Flex>
    </MotionBox>
  );
}
